import PageHeader from '../../../layout/page/PageHeader'
import { useAppSelector } from '../../../../store/hooks'
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { useCallback, useEffect, useState } from 'react'
import PageEndpoint from './endpoints/PageEndpoint'
import InitializeEndpoint from './endpoints/InitializeEndpoint'
import TranslateEndpoint from './endpoints/TranslateEndpoint'
import MediaEndpoint from './endpoints/MediaEndpoint'
import copy from 'copy-to-clipboard'
import { api } from '../../../../auth'
import Common from '../../../common/Common'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import RefreshApiKeyDialog from './dialogs/RefreshApiKeyDialog'
import TeamEndpoint from './endpoints/TeamEndpoint'
import PrivateAPIsForm from './private-apis/PrivateAPIsForm'
import SitemapEndpoint from './endpoints/SitemapEndpoint'

const APIManager = () => {
    const theme = useAppSelector((state) => state.theme.theme)

    const [accessLevel, setAccessLevel] = useState<
        string | 'public' | 'private'
    >('')
    const [apiVersion, setApiVersion] = useState<string | 'v1'>('v1')
    const [selectedEndpoint, setSelectedEndpoint] = useState<string>('')
    const [apiKey, setApiKey] = useState<string>('')
    const [apiKeyIsCopied, setApiKeyIsCopied] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const axiosPrivate = api

    const availableEndpoints: Record<string, any> = {
        public: {
            initialize: {
                name: 'Initialize',
                component: (
                    <InitializeEndpoint version={apiVersion} apiKey={apiKey} />
                ),
            },
            page: {
                name: 'Page',
                component: (
                    <PageEndpoint version={apiVersion} apiKey={apiKey} />
                ),
            },
            team: {
                name: 'Team',
                component: (
                    <TeamEndpoint version={apiVersion} apiKey={apiKey} />
                ),
            },
            media: {
                name: 'Media',
                component: <MediaEndpoint version={apiVersion} />,
            },
            sitemap: {
                name: 'Sitemap',
                component: (
                    <SitemapEndpoint version={apiVersion} apiKey={apiKey} />
                ),
            },
        },
        private: {
            translate: {
                name: 'Translate',
                component: (
                    <TranslateEndpoint version={apiVersion} apiKey={apiKey} />
                ),
            },
        },
    }

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/administration/api-manager')
                .finally(() => {
                    setIsLoading(false)
                })
            if (response.data?.success === true) {
                setApiKey(response.data?.data.api_key)
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            console.log(err?.response)
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else if (err.response?.data?.success === false) {
                setErrMsg(err.response?.data?.errors.join('<br />'))
            } else {
                setErrMsg('Failed to load API Manager!')
            }
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const copyToClipboard = () => {
        if (copy(apiKey)) {
            setApiKeyIsCopied(true)
            setTimeout(() => setApiKeyIsCopied(false), 3000)
        }
    }

    useEffect(() => {
        setSelectedEndpoint('')
        setAccessLevel('')
    }, [apiKey])

    return (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            <PageHeader
                icon="bolt"
                subtitle="Manage API keys, endpoints, and access control"
                gradient="linear-gradient(135deg, #ef4444, #dc2626)"
                header="API Manager"
                buttons={[
                    {
                        variant: theme === 'dark' ? 'dark' : 'light',
                        label: 'Back to Administration',
                        icon: 'fa-solid fa-arrow-left',
                        url: '/administration',
                    },
                ]}
            />
            <hr />
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <div>
                <fieldset>
                    <legend>APIs</legend>
                    <Row>
                        <Col md={12} lg={8} xxl={6}>
                            <InputGroup className="mb-3 sfy-field">
                                <Form.Label className="w-100">
                                    API Key
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={apiKey}
                                    className="readonly"
                                    readOnly
                                />
                                <RefreshApiKeyDialog setApiKey={setApiKey} />
                                <Button
                                    variant="outline-success"
                                    onClick={copyToClipboard}
                                >
                                    <i
                                        className={`fa-solid fa-${
                                            apiKeyIsCopied ? 'check' : 'copy'
                                        } me-2`}
                                    />
                                    Copy
                                </Button>
                            </InputGroup>
                        </Col>
                        <Col md={6} lg={4} xxl={2}>
                            <Form.Group>
                                <Form.Label className="w-100">
                                    Version
                                </Form.Label>
                                <Form.Select
                                    value={apiVersion}
                                    onChange={(e) => {
                                        setSelectedEndpoint('')
                                        setAccessLevel('')
                                        setApiVersion(e.target.value)
                                    }}
                                    aria-label="API version"
                                >
                                    <option value="v1">Version 1 (v1)</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} lg={6} xxl={2}>
                            <Form.Group>
                                <Form.Label className="w-100">
                                    Access Level
                                </Form.Label>
                                <Form.Select
                                    value={accessLevel}
                                    onChange={(e) => {
                                        setSelectedEndpoint('')
                                        setAccessLevel(e.target.value)
                                    }}
                                    aria-label="Access Level"
                                >
                                    <option value="">
                                        {' '}
                                        -- Select access level --{' '}
                                    </option>
                                    <option value="public">Public</option>
                                    <option value="private">Private</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} lg={6} xxl={2}>
                            <Form.Group>
                                <Form.Label className="w-100">
                                    Endpoint
                                </Form.Label>
                                <Form.Select
                                    disabled={!accessLevel}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setSelectedEndpoint(
                                                availableEndpoints[
                                                    accessLevel
                                                ]?.[e.target.value].component
                                            )
                                        } else {
                                            setSelectedEndpoint('')
                                        }
                                    }}
                                    aria-label="Available Endpoints"
                                >
                                    <option value="">
                                        {' '}
                                        -- Select endpoint --{' '}
                                    </option>
                                    {availableEndpoints[accessLevel] ? (
                                        Object.keys(
                                            availableEndpoints[accessLevel]
                                        ).map((k) => {
                                            const endpoint =
                                                availableEndpoints[accessLevel][
                                                    k
                                                ]
                                            return (
                                                <option key={k} value={k}>
                                                    {endpoint.name}
                                                </option>
                                            )
                                        })
                                    ) : (
                                        <></>
                                    )}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col
                            lg={accessLevel === 'private' ? 7 : 12}
                            xl={accessLevel === 'private' ? 8 : 12}
                            xxl={accessLevel === 'private' ? 9 : 12}
                            className="pt-2 pt-xl-0"
                        >
                            {selectedEndpoint}
                        </Col>
                        {accessLevel === 'private' ? (
                            <Col lg={5} xl={4} xxl={3} className="pt-2 pt-xl-0">
                                <PrivateAPIsForm />
                            </Col>
                        ) : (
                            <></>
                        )}
                    </Row>
                </fieldset>
            </div>
        </>
    )
}

export default APIManager
